/**
 * 答案归一化规则集（宽松为默认）。
 *
 * 设计要点：每条规则都是一个「规范化变换」，同时作用在用户答案与所有可接受答案上。
 * 逐条规则叠加后比较，**第一条使两者相等的规则**即记为 matched_via，
 * 用于复盘页提示"判对（忽略冠词差异）"这类信息（见 PLAN 5.4）。
 *
 * 明确不做的事（宽松的边界）：语义等价、同义推断、子串匹配、编辑距离容错。
 */
export const NORMALIZATION_RULE_IDS = [
    'trim',
    'collapse_ws',
    'case_insensitive',
    'punctuation_ignore',
    'ignore_articles',
    'plural_tolerant',
    'hyphen_space',
    'numeric_equivalence',
    'british_american',
    'ordinal_equivalence',
    'cardinal_word_equivalence',
    'abbreviation_expand',
    'grammar_inflection',
    'tfng_alias',
];
export const NORMALIZATION_PROFILE_IDS = [
    'tfng',
    'ynng',
    'letter',
    'roman',
    'one-word',
    'short-answer',
    'exact',
];
/* ------------------------------------------------------------------ *
 * 词表
 * ------------------------------------------------------------------ */
/** 英美拼写对照（英式 -> 美式）。两侧都会被规范化成美式。 */
const BRITISH_TO_AMERICAN = {
    colour: 'color',
    colours: 'colors',
    coloured: 'colored',
    colourful: 'colorful',
    centre: 'center',
    centres: 'centers',
    centralise: 'centralize',
    metre: 'meter',
    metres: 'meters',
    litre: 'liter',
    litres: 'liters',
    labour: 'labor',
    favour: 'favor',
    favourite: 'favorite',
    neighbour: 'neighbor',
    neighbours: 'neighbors',
    behaviour: 'behavior',
    behaviours: 'behaviors',
    travelling: 'traveling',
    travelled: 'traveled',
    traveller: 'traveler',
    cancelled: 'canceled',
    cancelling: 'canceling',
    defence: 'defense',
    licence: 'license',
    practise: 'practice',
    grey: 'gray',
    aluminium: 'aluminum',
    catalogue: 'catalog',
    dialogue: 'dialog',
    cheque: 'check',
    tyre: 'tire',
    storey: 'story',
    mould: 'mold',
    plough: 'plow',
    sceptic: 'skeptic',
    sceptical: 'skeptical',
    aeroplane: 'airplane',
    kerb: 'curb',
    draught: 'draft',
    jewellery: 'jewelry',
    enrolment: 'enrollment',
    fulfil: 'fulfill',
    skilful: 'skillful',
    modelling: 'modeling',
    marvellous: 'marvelous',
    woollen: 'woolen',
    ageing: 'aging',
    judgement: 'judgment',
    acknowledgement: 'acknowledgment',
};
/** 反向索引：美式写法也指向自己（保证两侧都落到同一形态）。 */
const SPELLING_CANON = (() => {
    const map = {};
    for (const [british, american] of Object.entries(BRITISH_TO_AMERICAN)) {
        map[british] = american;
        map[american] = american;
    }
    return map;
})();
const SPELLING_SUFFIXES = [
    [/isation$/, 'ization'],
    [/isations$/, 'izations'],
    [/ise$/, 'ize'],
    [/ised$/, 'ized'],
    [/ises$/, 'izes'],
    [/ising$/, 'izing'],
    [/iser$/, 'izer'],
    [/yse$/, 'yze'],
    [/ysed$/, 'yzed'],
    [/yses$/, 'yzes'],
    [/ysing$/, 'yzing'],
];
const UNITS = {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    thirteen: 13,
    fourteen: 14,
    fifteen: 15,
    sixteen: 16,
    seventeen: 17,
    eighteen: 18,
    nineteen: 19,
};
const TENS = {
    twenty: 20,
    thirty: 30,
    forty: 40,
    fifty: 50,
    sixty: 60,
    seventy: 70,
    eighty: 80,
    ninety: 90,
};
const SCALES = { hundred: 100, thousand: 1000 };
const ORDINAL_WORDS = {
    first: '1',
    second: '2',
    third: '3',
    fourth: '4',
    fifth: '5',
    sixth: '6',
    seventh: '7',
    eighth: '8',
    ninth: '9',
    tenth: '10',
    eleventh: '11',
    twelfth: '12',
    thirteenth: '13',
    fourteenth: '14',
    fifteenth: '15',
    sixteenth: '16',
    seventeenth: '17',
    eighteenth: '18',
    nineteenth: '19',
    twentieth: '20',
};
const ABBREVIATIONS = {
    uk: 'united kingdom',
    usa: 'united states',
    us: 'united states',
    uae: 'united arab emirates',
    dr: 'doctor',
    prof: 'professor',
    eg: 'for example',
    ie: 'that is',
    etc: 'et cetera',
    'per cent': 'percent',
};
const TFNG_ALIASES = {
    t: 'true',
    true: 'true',
    f: 'false',
    false: 'false',
    ng: 'notgiven',
    notgiven: 'notgiven',
    notgn: 'notgiven',
    y: 'yes',
    yes: 'yes',
    n: 'no',
    no: 'no',
};
/* ------------------------------------------------------------------ *
 * 规则实现
 * ------------------------------------------------------------------ */
function singularize(word) {
    if (word.length <= 3)
        return word;
    if (word.endsWith('ies'))
        return `${word.slice(0, -3)}y`;
    if (/(ses|xes|zes|ches|shes)$/.test(word))
        return word.slice(0, -2);
    if (word.endsWith('s') && !word.endsWith('ss'))
        return word.slice(0, -1);
    return word;
}
function stripInflection(word) {
    if (word.length <= 4)
        return word;
    if (word.endsWith('ies'))
        return `${word.slice(0, -3)}y`;
    if (word.endsWith('ing'))
        return word.slice(0, -3);
    if (word.endsWith('ed'))
        return word.slice(0, -2);
    if (word.endsWith('es'))
        return word.slice(0, -2);
    if (word.endsWith('s') && !word.endsWith('ss'))
        return word.slice(0, -1);
    return word;
}
function toAmericanSpelling(word) {
    const known = SPELLING_CANON[word];
    if (known)
        return known;
    for (const [pattern, replacement] of SPELLING_SUFFIXES) {
        if (pattern.test(word))
            return word.replace(pattern, replacement);
    }
    return word;
}
function wordsToDigits(text) {
    const tokens = text.split(/\s+/).filter((t) => t.length > 0);
    if (tokens.length === 0)
        return text;
    let total = 0;
    let current = 0;
    let matched = false;
    for (const token of tokens) {
        if (token === 'and')
            continue;
        if (token in UNITS) {
            current += UNITS[token];
            matched = true;
        }
        else if (token in TENS) {
            current += TENS[token];
            matched = true;
        }
        else if (token in SCALES) {
            const scale = SCALES[token];
            current = (current === 0 ? 1 : current) * scale;
            if (scale >= 1000) {
                total += current;
                current = 0;
            }
            matched = true;
        }
        else {
            return text;
        }
    }
    if (!matched)
        return text;
    return String(total + current);
}
function tfngCanonical(text) {
    const compact = text.replace(/\s+/g, '');
    return TFNG_ALIASES[compact] ?? compact;
}
const RULES = {
    trim: (s) => s.trim(),
    collapse_ws: (s) => s.replace(/\s+/g, ' ').trim(),
    case_insensitive: (s) => s.toLowerCase(),
    // 先把百分号与 & 变成词，再删掉其余标点，避免 "50%" 与 "50 percent" 被判为不同
    punctuation_ignore: (s) => s
        .replace(/%/g, ' percent ')
        .replace(/&/g, ' and ')
        .replace(/[^\p{L}\p{N}\s]/gu, '')
        .replace(/\s+/g, ' ')
        .trim(),
    ignore_articles: (s) => s
        .replace(/\b(a|an|the)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
    plural_tolerant: (s) => s.split(' ').map(singularize).join(' '),
    hyphen_space: (s) => s.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim(),
    numeric_equivalence: (s) => s.replace(/(?<=\d)[,\s](?=\d)/g, ''),
    british_american: (s) => s.split(' ').map(toAmericanSpelling).join(' '),
    ordinal_equivalence: (s) => s
        .replace(/\b(\d+)(st|nd|rd|th)\b/g, '$1')
        .split(' ')
        .map((w) => ORDINAL_WORDS[w] ?? w)
        .join(' '),
    cardinal_word_equivalence: (s) => wordsToDigits(s),
    abbreviation_expand: (s) => s
        .split(' ')
        .map((w) => ABBREVIATIONS[w] ?? w)
        .join(' '),
    grammar_inflection: (s) => s.split(' ').map(stripInflection).join(' '),
    tfng_alias: (s) => tfngCanonical(s),
};
/* ------------------------------------------------------------------ *
 * Profile
 * ------------------------------------------------------------------ */
export const PROFILES = {
    tfng: ['trim', 'collapse_ws', 'case_insensitive', 'punctuation_ignore', 'tfng_alias'],
    ynng: ['trim', 'collapse_ws', 'case_insensitive', 'punctuation_ignore', 'tfng_alias'],
    letter: ['trim', 'collapse_ws', 'case_insensitive', 'punctuation_ignore'],
    roman: ['trim', 'collapse_ws', 'case_insensitive', 'punctuation_ignore'],
    'one-word': [
        'trim',
        'collapse_ws',
        'case_insensitive',
        'punctuation_ignore',
        'ignore_articles',
        'plural_tolerant',
        'hyphen_space',
        'numeric_equivalence',
        'british_american',
        'ordinal_equivalence',
        'cardinal_word_equivalence',
        'abbreviation_expand',
    ],
    'short-answer': [
        'trim',
        'collapse_ws',
        'case_insensitive',
        'punctuation_ignore',
        'ignore_articles',
        'plural_tolerant',
        'hyphen_space',
        'numeric_equivalence',
        'british_american',
        'ordinal_equivalence',
        'cardinal_word_equivalence',
        'abbreviation_expand',
        'grammar_inflection',
    ],
    exact: ['trim'],
};
/** 默认 profile：宽松。 */
export const DEFAULT_PROFILE = 'one-word';
export const PROFILE_LABELS = {
    tfng: '判断题宽松匹配',
    ynng: '判断题宽松匹配',
    letter: '选项字母匹配',
    roman: '罗马数字匹配',
    'one-word': '填空宽松匹配',
    'short-answer': '简答宽松匹配',
    exact: '精确匹配',
};
/** 规则的中文说明，用于复盘页展示"因某规则判对"。 */
export const RULE_LABELS = {
    trim: '去首尾空白',
    collapse_ws: '空白归一',
    case_insensitive: '忽略大小写',
    punctuation_ignore: '忽略标点',
    ignore_articles: '忽略冠词',
    plural_tolerant: '单复数互通',
    hyphen_space: '连字符与空格互通',
    numeric_equivalence: '数字格式互通',
    british_american: '英美拼写互通',
    ordinal_equivalence: '序数互通',
    cardinal_word_equivalence: '数词与数字互通',
    abbreviation_expand: '缩写展开',
    grammar_inflection: '词形变化互通',
    tfng_alias: '判断题简写展开',
};
const RULE_ID_SET = new Set(NORMALIZATION_RULE_IDS);
/** 解析出最终生效的规则序列（题级 normalization 优先于 profile）。 */
export function resolveRules(input) {
    let rules;
    if (input.normalization && input.normalization.length > 0) {
        if (input.normalization.includes('exact')) {
            rules = ['trim'];
        }
        else {
            rules = input.normalization.filter((r) => RULE_ID_SET.has(r));
            if (rules.length === 0)
                rules = [...PROFILES[DEFAULT_PROFILE]];
        }
    }
    else {
        rules = [...PROFILES[input.profile ?? DEFAULT_PROFILE]];
    }
    if (input.caseSensitive) {
        rules = rules.filter((r) => r !== 'case_insensitive');
    }
    return rules;
}
/** 按规则序列逐步规范化并比较；返回命中的第一条规则。 */
export function normalizeAndCompare(userAnswer, accepted, rules) {
    let user = userAnswer.normalize('NFKC').trim();
    let list = accepted.map((a) => a.normalize('NFKC').trim());
    if (user.length === 0) {
        return { isCorrect: false, matchedVia: 'empty', normalizedUser: user, normalizedAccepted: list };
    }
    if (list.includes(user)) {
        return { isCorrect: true, matchedVia: 'exact', normalizedUser: user, normalizedAccepted: list };
    }
    for (const rule of rules) {
        const fn = RULES[rule];
        user = fn(user);
        list = list.map(fn);
        if (list.includes(user)) {
            return { isCorrect: true, matchedVia: rule, normalizedUser: user, normalizedAccepted: list };
        }
    }
    return { isCorrect: false, matchedVia: 'none', normalizedUser: user, normalizedAccepted: list };
}
//# sourceMappingURL=rules.js.map